<template>
  <div class="album-photos" :class="{ desktop: $isDesktop }">
    <div
      v-for="(photos, tag, index) in galleriesToShow"
      :key="tag"
      class="tag-gallery"
    >
      <h2>{{ idToLabelMap.tags[tag] }}</h2>
      <div class="gallery-preview">
        <div
          v-for="(photo, idx) in photos.slice(0, previwGalNoImages)"
          :key="`${selectedTag}-${idx}`"
          class="gallery-item"
        >
          <img
            loading="lazy"
            width="400"
            height="300"
            :src="`/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=70,w=400,h=300${photo.url.masterImage}`"
            @click="openGallery(tag, idx + 1)"
          />
          <span v-if="photo.label" class="gallery-photo-label">{{
            photo.label
          }}</span>
        </div>
      </div>
      <div
        v-show="expandedAlbums[selectedTag + index]"
        class="gallery-preview additional-photos"
      >
        <div
          v-for="(photo, idx) in photos.slice(previwGalNoImages)"
          :key="`${selectedTag}-${idx}`"
          class="gallery-item"
        >
          <img
            loading="lazy"
            width="400"
            height="300"
            :src="`/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=70,w=400,h=300${photo.url.masterImage}`"
            @click="openGallery(tag, idx + previwGalNoImages + 1)"
          />
          <span v-if="photo.label" class="gallery-photo-label">{{
            photo.label
          }}</span>
        </div>
      </div>

      <button
        v-if="photos.length > previwGalNoImages"
        type="button"
        :class="[
          expandedAlbums[selectedTag + index] ? 'view-less' : 'view-more',
        ]"
        @click.prevent="showMorePhotos(index)"
      >
        <img
          loading="lazy"
          src="@/assets/images/forward-rounded-dark-blue.svg"
          width="17"
          height="30"
          alt=""
        />
        <translate v-if="expandedAlbums[selectedTag + index]">
          View fewer
        </translate>
        <translate v-else :tt="{ count: photos.length }">
          View all ({count})
        </translate>
      </button>
    </div>
    <inline-gallery
      v-if="albumId && galleriesToShow && galleriesToShow[albumId]"
      :photos="galleriesToShow[albumId]"
      :primary-photo="galleriesToShow[albumId][0]"
      :fullscreen="true"
      :fullscreen-open="true"
      :gallery-initial-index="initialIndex"
      :limit-initial-gallery-photos="false"
      @slide-changed="slideChanged"
      @fullscreen-closed="closeGallery"
    />
  </div>
</template>
<script setup lang="ts">
import { useNuxtApp, useRoute, useRouter } from 'nuxt/app'
import { logger } from '../../utility/logger'
const { $routerClient } = useNuxtApp()

const props = defineProps<{
  photosByTag: Object
  primaryPhoto?: Object
  idToLabelMap: Object
}>()

const route = useRoute()
const router = useRouter()

const selectedTag = computed(() => {
  return route.params.tag || 'all'
})

const galleriesToShow = computed(() => {
  logger('galleriesToShow', selectedTag.value)
  if (selectedTag.value === 'pitchtypes') {
    return props.photosByTag.pitchtypes
  }

  if (selectedTag.value === 'onsite-accommodation') {
    return props.photosByTag.onsite
  }

  if (selectedTag.value === 'visitor') {
    return props.photosByTag.visitor
  }

  if (selectedTag.value !== undefined && selectedTag.value !== 'all') {
    return props.photosByTag.topLevel[selectedTag.value]
  }

  return props.photosByTag.allPhotos
})

const previwGalNoImages = computed(() => {
  if (Object.keys(galleriesToShow.value).length > 1) {
    return 4
  } else {
    return 24
  }
})

const albumId = computed(() => {
  return route.params.albumId
})

const initialIndex = computed(() => {
  return parseInt(route.query.slideId) - 1
})

/**
 * Expanding albums
 */
const expandedAlbums = ref({})
function showMorePhotos(index) {
  const key = selectedTag.value + index
  expandedAlbums.value[key] = !expandedAlbums.value[key]
}

/**
 * Navigation functions
 */
function openGallery(tag, slideId) {
  logger('openGallery', tag, slideId)
  void router.push({
    name: 'campsite-albums-album',
    params: {
      ...route.params,
      tag: route.params.tag || 'all',
      albumId: tag,
    },
    query: {
      ...route.query,
      slideId,
    },
  })
}

function closeGallery(tag, slideId) {
  logger('closeGallery', tag, slideId)
  if ($routerClient.getReferrer()) {
    router.go(-1)
  } else {
    void router.push({
      name: 'campsite-albums-album',
      params: {
        ...route.params,
        tag: route.params.tag || 'all',
        albumId: undefined,
      },
      query: undefined,
    })
  }
}

function slideChanged(slideId) {
  const query = Object.entries(route.query)
    .filter(([key, value]) => key !== 'slideId')
    .reduce((queryString, [key, value]) => {
      queryString += `&${key}=${value}`
      return queryString
    }, '')
  history.replaceState({}, undefined, `?slideId=${slideId}${query}`)
}
</script>

<style scoped>
h2 {
  font-size: var(--body);
  color: var(--colour-dark-blue);
}

.tag-gallery {
  padding: 1em;
}

.gallery-preview {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  grid-auto-rows: auto;
  gap: 1em;
}

.gallery-item {
  overflow: hidden;
  background: #f4f6f8;
  border-radius: calc(var(--border-radius) / 2);
  display: flex;
  flex-direction: column;
}

.gallery-preview img {
  overflow: clip;
  cursor: pointer;
  width: 100%;
  height: 100%;
  object-fit: contain;
  max-height: 133px;
}

.gallery-photo-label {
  display: block;
  font-size: var(--small);
  padding: 0.4em 0.2em;
  line-height: 1;
  align-items: center;
  justify-content: center;
  text-align: center;
}

button {
  align-items: center;
  border-radius: 6em;
  box-shadow: none;
  color: var(--colour-dark-blue);
  display: flex;
  gap: 0.3em;
  justify-content: flex-start;
  margin: 0.6em 0 0 auto;
  padding: 0.3em 0.5em;
  border: 1px solid #999;
  font-size: var(--small);
}

button:active {
  background: var(--colour-dark-blue);
  border-color: var(--colour-dark-blue);
  color: #fff;
  box-shadow: none;
}

button.view-less img {
  transform: rotate(-90deg);
}

button img {
  transform: rotate(90deg);
  height: 1em;
  transition: transform 0.2s ease-in;
}

button:active img {
  filter: invert(1) brightness(2);
}

.additional-photos {
  padding-top: 1em;
}

/* desktop & tablet */
.album-photos.desktop {
  margin: auto;
  max-width: var(--page-width-max);
  padding: 0 var(--page-width-padding) 3em;
}

.album-photos.desktop .tag-gallery {
  padding: 2em 1em;
}

.album-photos.desktop h2 {
  font-size: 1.3em;
  margin-bottom: 0.75em;
}

.album-photos.desktop button {
  gap: 0.5em;
  margin: 1em 0 0 auto;
  padding: 0.4em 0.7em;
  font-size: initial;
}

.album-photos.desktop .gallery-preview {
  grid-template-columns: 1fr 1fr 1fr 1fr;
}

.album-photos.desktop .gallery-photo-label {
  font-size: inherit;
  padding: 0.5em;
}

.album-photos.desktop .gallery-preview img {
  max-height: 197px;
}

@media (hover: hover) {
  button:hover {
    background: var(--colour-dark-blue);
    border-color: var(--colour-dark-blue);
    color: #fff;
    box-shadow: none;
  }

  button:hover img {
    filter: invert(1) brightness(2);
  }
}

@media (max-width: 360px) {
  .gallery-preview img {
    max-height: 245px;
  }
}
</style>
